/**
 * Lightweight searchable single-select dropdown — replaces native <select>
 * when the option list is too long to scroll comfortably (esp. on mobile,
 * where the iOS native picker is unusable past ~50 entries).
 *
 * Filter is case- and diacritic-insensitive, matches against the visible
 * label and an optional secondary string (typically a phone number).
 *
 * Usage:
 *   <SearchablePicker
 *     value={form.clientId}
 *     onChange={(id) => set('clientId', id)}
 *     placeholder="Verkäufer wählen…"
 *     options={clients.map((c) => ({
 *       value: c.id,
 *       label: `${c.first_name} ${c.last_name}`,
 *       secondary: c.phone,
 *     }))}
 *     required
 *   />
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'lucide-react';

export interface PickerOption {
  value: string;
  label: string;
  secondary?: string;
}

export interface SearchablePickerProps {
  value: string;
  onChange: (v: string) => void;
  options: PickerOption[];
  placeholder?: string;
  emptyText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

function normalize(s: string): string {
  return s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');
}

export function SearchablePicker({
  value,
  onChange,
  options,
  placeholder,
  emptyText,
  required,
  disabled,
  className = '',
}: SearchablePickerProps) {
  const { t } = useTranslation();
  placeholder = placeholder ?? t('common.picker.select', 'Wählen …');
  emptyText = emptyText ?? t('common.picker.no_results', 'Keine Treffer');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!query) return options.slice(0, 50);
    const q = normalize(query);
    return options
      .filter((o) =>
        normalize(o.label).includes(q) ||
        (o.secondary ? normalize(o.secondary).includes(q) : false),
      )
      .slice(0, 50);
  }, [options, query]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // focus search field when opened
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground transition-colors hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className={`truncate text-start ${selected ? '' : 'text-ink-3'}`}>
          {selected ? (
            <>
              {selected.label}
              {selected.secondary && (
                <span className="ms-2 font-mono text-xs text-ink-3">
                  · {selected.secondary}
                </span>
              )}
            </>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-ink-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Hidden native input for `required` form validation */}
      {required && (
        <input
          tabIndex={-1}
          required
          value={value}
          onChange={() => undefined}
          aria-hidden
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            height: 1,
            width: 1,
            bottom: 0,
            left: 0,
          }}
        />
      )}

      {open && (
        <div className="absolute start-0 end-0 z-50 mt-1 max-h-[60vh] overflow-hidden rounded-md border border-line bg-card shadow-strong">
          <div className="flex items-center gap-2 border-b border-line bg-paper-2 px-3 py-2">
            <Search className="h-4 w-4 flex-shrink-0 text-ink-3" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full border-0 bg-transparent text-sm text-ink outline-none placeholder:text-ink-3"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-ink-3 hover:text-ink"
                aria-label={t('common.picker.clear_search', 'Suche zurücksetzen')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-ink-3">{emptyText}</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-start text-sm transition-colors hover:bg-paper-2 ${
                    o.value === value ? 'bg-paper-2 font-medium text-ink' : 'text-ink-2'
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.secondary && (
                    <span className="font-mono text-xs text-ink-3">{o.secondary}</span>
                  )}
                </button>
              ))
            )}
            {options.length > 50 && !query && (
              <p className="border-t border-line px-3 py-2 text-center text-[11px] text-ink-3">
                {options.length} insgesamt — Suche eingrenzen
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
